// src/context/ToolContext.tsx
import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import type { WildfireEntity } from '../types/wildfire'; // adjust path if needed
import type { VAREntity } from '../types/var';
import type { FlagType } from '../components/explorers/varExplorer/VARFlagDialog';

// Single source of truth: add new tools here and they automatically work
// everywhere — type checking, initial state, AND ?tool= deep linking.
export const TOOL_KEYS = [
  'dutylogs',
  'weatherStations',
  'weatherData',
  'wildfire',
  'lightning',
  'aff',
  'hotspotExplorer',
  'latlong',
  'userEdit',
  'passwordReset',
  'wildfireDetail',
  'support',
  'reportIssue',
  'requestFeature',
  'viewIssues',
  'onLogout',
  'printMap',
  'themePreferences',
  'layerAdmin',
  'layers',
  'privateFileTest',
  'fireModeling',
  'quickZoom',
  'placenameTool',
  'myLocation',
  'getFeatureInfo',
  'spotWx',
  'varExplorer',
  'varFlagDialog',
  'auditExplorer',
  'permissionManager',
  'reIssueReports',
  'gisDownload',
  'rfbf',
] as const;

export type ToolKey = typeof TOOL_KEYS[number];

/** Runtime check: is this string a valid ToolKey? Supports ?tool= deep linking. */
export const isToolKey = (s: string): s is ToolKey =>
  (TOOL_KEYS as readonly string[]).includes(s);

export type ToolPayloads = {
  dutylogs: { fireNumber?: string; crosslinkType?: string; initialCrosslinkSearch?: { term: string; type: string } };
  wildfire: {
    initialFilters?: Array<{
      field: string;
      operator: string;
      value: any;
    }>;
    regionFilter?: string;
  };
  lightning: Record<string, never>;
  aff: { context?: 'aircraft' | 'tracks' };
  hotspotExplorer: Record<string, never>;
  wildfireDetail: {
    wildfire?: WildfireEntity;
    fireId?: string;
    tab?: string;
  };
  weatherStations: { stationId?: string };
  weatherData: { source?: string; stationId?: string; startDate?: string; endDate?: string };
  latlong: Record<string, never>;
  userEdit: Record<string, never>;
  passwordReset: Record<string, never>;
  onLogout: Record<string, never>;
  printMap: Record<string, never>;
  themePreferences: Record<string, never>;
  layerAdmin: { layerId?: string };
  fireModeling: { modelId?: string; mode?: 'new' | 'view' };
  support: Record<string, never>;
  reportIssue: Record<string, never>;
  requestFeature: Record<string, never>;
  viewIssues: Record<string, never>;
  layers: Record<string, never>;
  privateFileTest: Record<string, never>;
  quickZoom: Record<string, never>;
  placenameTool: Record<string, never>;
  myLocation: Record<string, never>;
  getFeatureInfo: Record<string, never>;
  spotWx: Record<string, never>;
  varExplorer: { varId?: string };
  varFlagDialog: { entity: VAREntity; flagType: FlagType };
  auditExplorer: Record<string, never>;
  permissionManager: Record<string, never>;
  reIssueReports: Record<string, never>;
  gisDownload: Record<string, never>;
  rfbf: Record<string, never>;
};

type ToolState = Record<ToolKey, boolean>;

interface ToolContextValue {
  tools: ToolState;
  payloads: Partial<ToolPayloads>;
  openTool: <K extends ToolKey>(key: K, payload?: ToolPayloads[K]) => void;
  closeTool: (key: ToolKey) => void;
}

const ToolContext = createContext<ToolContextValue | null>(null);

export const ToolProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tools, setTools] = useState<ToolState>(
    Object.fromEntries(TOOL_KEYS.map(k => [k, false])) as ToolState
  );

  const [payloads, setPayloads] = useState<Partial<ToolPayloads>>({});

  // Sync getFeatureInfo state with external changes (from map control button)
  useEffect(() => {
    const handleExternalChange = ((e: CustomEvent) => {
      // Only update if the change came from outside ToolContext
      if (e.detail.source !== 'toolContext') {
        setTools((prev) => ({ ...prev, getFeatureInfo: e.detail.enabled }));
      }
    }) as EventListener;

    window.addEventListener('intellifire:get-feature-info', handleExternalChange);
    return () => {
      window.removeEventListener('intellifire:get-feature-info', handleExternalChange);
    };
  }, []);

  // Sync spotWx state with external changes (from map control button)
  useEffect(() => {
    const handleExternalChange = ((e: CustomEvent) => {
      // Only update if the change came from outside ToolContext
      if (e.detail.source !== 'toolContext') {
        setTools((prev) => ({ ...prev, spotWx: e.detail.enabled }));
      }
    }) as EventListener;

    window.addEventListener('intellifire:spotwx', handleExternalChange);
    return () => {
      window.removeEventListener('intellifire:spotwx', handleExternalChange);
    };
  }, []);

  // Listen for varFlagToggle events from VARLayer popup (map → React bridge)
  useEffect(() => {
    const handleVarFlagToggle = ((e: CustomEvent<{ entity: VAREntity; flagType: FlagType }>) => {
      const { entity, flagType } = e.detail;
      setPayloads((prev) => ({ ...prev, varFlagDialog: { entity, flagType } }));
      setTools((prev) => ({ ...prev, varFlagDialog: true }));
    }) as EventListener;

    window.addEventListener('varFlagToggle', handleVarFlagToggle);
    return () => {
      window.removeEventListener('varFlagToggle', handleVarFlagToggle);
    };
  }, []);

  // Listen for openVarExplorer events from VARLayer popup
  useEffect(() => {
    const handleOpenVarExplorer = ((e: CustomEvent<{ varId: string }>) => {
      const { varId } = e.detail;
      setPayloads((prev) => ({ ...prev, varExplorer: { varId } }));
      setTools((prev) => ({ ...prev, varExplorer: true }));
    }) as EventListener;

    window.addEventListener('openVarExplorer', handleOpenVarExplorer);
    return () => {
      window.removeEventListener('openVarExplorer', handleOpenVarExplorer);
    };
  }, []);

  const openTool = useCallback(<K extends ToolKey>(key: K, payload?: ToolPayloads[K]) => {

    if (key === 'onLogout') {
      window.location.href = '/logout';
      return;
    }

    if (key === 'layers') {
      window.dispatchEvent(new CustomEvent('intellifire:open-layer-drawer'));
      return;
    }

    if (key === 'quickZoom') {
      window.dispatchEvent(new CustomEvent('intellifire:open-quick-zoom'));
      return;
    }

    if (key === 'placenameTool') {
      window.dispatchEvent(new CustomEvent('intellifire:open-placename-tool'));
      return;
    }

    if (key === 'myLocation') {
      window.dispatchEvent(new CustomEvent('intellifire:my-location'));
      return;
    }

    // GetFeatureInfo is a toggle - flip the state and dispatch event
    if (key === 'getFeatureInfo') {
      setTools((prev) => {
        const newState = !prev.getFeatureInfo;
        window.dispatchEvent(new CustomEvent('intellifire:get-feature-info', {
          detail: { enabled: newState, source: 'toolContext' }
        }));
        return { ...prev, getFeatureInfo: newState };
      });
      return;
    }

    // SpotWx is a toggle - flip the state and dispatch event
    if (key === 'spotWx') {
      setTools((prev) => {
        const newState = !prev.spotWx;
        window.dispatchEvent(new CustomEvent('intellifire:spotwx', {
          detail: { enabled: newState, source: 'toolContext' }
        }));
        return { ...prev, spotWx: newState };
      });
      return;
    }

    setTools((prev) => ({ ...prev, [key]: true }));
    if (payload) {
      setPayloads((prev) => ({ ...prev, [key]: payload }));
    }
  }, []);

  const closeTool = useCallback((key: ToolKey) => {
    setTools((prev) => ({ ...prev, [key]: false }));
    setPayloads((prev) => {
      const next = { ...prev };
      delete (next as any)[key];
      return next;
    });
    // Clear query params using history API (doesn't trigger navigation)
    const url = new URL(window.location.href);
    url.search = ''; // Clear all query params
    window.history.replaceState({}, '', url.pathname);
  }, []);

  return (
    <ToolContext.Provider value={{ tools, payloads, openTool, closeTool }}>
      {children}
    </ToolContext.Provider>
  );
};

export const useTools = () => {
  const ctx = useContext(ToolContext);
  if (!ctx) {
    throw new Error('useTools must be used inside <ToolProvider>');
  }
  return ctx;
};
